/**
 * Pushing the zones to an aircraft as its own fence (api/app/routers/fence.py).
 *
 * A zone on the map is enforced on the ground only: a route or a goto through
 * it is refused before it is sent. A fence the autopilot holds is enforced with
 * the link down, which is when RTL flies a straight line home through whatever
 * is in the way. Nothing pushes on its own; an operator who commands the
 * aircraft does, one aircraft at a time.
 */

import { api, type ApiResult } from '@/api/client';
import type { DroneId } from '@/types/domain';

/** One word for whether an aircraft is holding the zones drawn now. */
export type FenceState =
  | 'enforcing'
  | 'report-only'
  | 'breached'
  | 'stale'
  | 'off'
  | 'unverified'
  | 'not-pushed'
  | 'failed'
  | 'no-zones';

export interface FencePushRecord {
  ok: boolean;
  message: string;
  zoneCount: number;
  itemCount: number;
  /** Seconds since the epoch. */
  at: number;
  by: string;
  fenceEnable: number | null;
  fenceType: number | null;
  fenceAction: number | null;
}

export interface FenceDrone {
  droneId: DroneId;
  callsign: string;
  simulated: boolean;
  /** Link state, as the snapshot reports it. */
  link: string;
  state: FenceState;
  detail: string;
  lastPush: FencePushRecord | null;
  /** Whether this user may push to it: operator and control of the aircraft. */
  canPush: boolean;
}

export interface FenceStatus {
  zoneCount: number;
  itemCount: number;
  /** A zone that cannot be expressed as a fence, named, or null. */
  zoneError: string | null;
  drones: FenceDrone[];
}

export function getFenceStatus(): Promise<ApiResult<FenceStatus>> {
  return api.get<FenceStatus>('/fence', { logged: false });
}

/**
 * Make this aircraft's fence areas match the zones drawn now.
 *
 * `enable` switches its fence on when it is off. Left false, the aircraft's
 * FENCE_ENABLE is not touched, so an operator who keeps the fence off on
 * purpose is not overruled by someone pushing zones.
 */
export function pushFence(
  droneId: DroneId,
  enable: boolean,
): Promise<ApiResult<FenceDrone & { ok: boolean; message: string }>> {
  return api.post<FenceDrone & { ok: boolean; message: string }>(
    `/fence/${encodeURIComponent(droneId)}`,
    { enable },
    { label: `FENCE PUSH ${droneId}${enable ? ' + ENABLE' : ''}` },
  );
}
